import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Icons } from '../../../shared/helpers/icons';
import {
  ActionUser,
  Agent,
  AgentSavePayload,
  FleetLayer,
  FleetLevel,
  Geofence,
  LayerDetail,
  LayerIcon,
  Style,
  StyleAction,
  StyleActionCreatePayload,
  StyleRule,
  StyleRuleCreatePayload,
  StyleUpdatePayload
} from '../../../shared/models/agent';
import { BuildingContextService } from '../../../shared/services/building-context.service';
import { FleetService } from '../../../shared/services/fleet.service';
import { SpinnerComponent } from '../../spinner/spinner.component';
import {
  LayerModalComponent,
  LayerModalMode,
  LayerModalSavePayload
} from '../../components/layer-modal/layer-modal.component';
import {
  TrackerModalComponent,
  TrackerModalMode
} from '../../components/tracker-modal/tracker-modal.component';
import {
  StyleModalComponent,
  StyleModalMode
} from '../../components/style-modal/style-modal.component';

@Component({
  selector: 'app-fleet',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideAngularModule,
    SpinnerComponent,
    LayerModalComponent,
    TrackerModalComponent,
    StyleModalComponent
  ],
  templateUrl: './fleet.component.html',
  styleUrl: './fleet.component.css'
})
export class FleetComponent {
  icons = Icons;

  // ----------------------------------------------------------------------
  // Building / tab state
  // ----------------------------------------------------------------------

  readonly selectedBuilding = computed(() => this.buildingContext.selected());

  levels = signal<FleetLevel[]>([]);
  loadingLevels = signal(false);
  /** 'trackers' = the All Trackers pane; a number = level id whose layers
   *  are visible. */
  activeTab = signal<'trackers' | number>('trackers');
  layersByLevel = signal<Record<number, FleetLayer[]>>({});
  loadingLayersForLevelId = signal<number | null>(null);
  buildingLayers = signal<FleetLayer[]>([]);
  loadingBuildingLayers = signal(false);
  agents = signal<Agent[]>([]);
  loadingAgents = signal(false);

  // ----------------------------------------------------------------------
  // Modal state. Each child owns its form internals — the parent only
  // tracks open/saving/loading flags + the currently-bound entity.
  // ----------------------------------------------------------------------

  // Tracker modal — single shell for both Add and Edit. `currentEditAgent`
  // is only populated in 'edit' mode.
  trackerModalOpen = signal(false);
  trackerModalSaving = signal(false);
  trackerModalMode = signal<TrackerModalMode>('create');
  currentEditAgent = signal<Agent | null>(null);

  // Layer modal — single shell for both Add and Edit. `currentEditLayer`
  // is only populated in 'edit' mode.
  layerModalOpen = signal(false);
  layerModalSaving = signal(false);
  layerModalLoading = signal(false);
  layerModalMode = signal<LayerModalMode>('add');
  currentEditLayer = signal<LayerDetail | null>(null);
  /** Assignable agents shared by both modes. In add mode the parent fetches
   *  with `currentAgentId=null`; in edit mode it passes the layer's current
   *  agent so that selection stays in the list. */
  assignableAgents = signal<Agent[]>([]);

  // Styles (list under the Edit-Layer modal)
  layerStyles = signal<Style[]>([]);
  loadingLayerStyles = signal(false);

  // Style modal — single shell for both Add and Edit. `currentEditStyle`
  // is only populated in 'edit' mode.
  styleModalOpen = signal(false);
  styleModalSaving = signal(false);
  styleModalMode = signal<StyleModalMode>('add');
  currentEditStyle = signal<Style | null>(null);

  // Rules + Actions populated when Edit Style opens — fed straight into
  // the UpdateStyleComponent via @Input.
  styleRules = signal<StyleRule[]>([]);
  styleActions = signal<StyleAction[]>([]);
  loadingStyleRules = signal(false);
  loadingStyleActions = signal(false);
  ruleSaving = signal(false);
  actionSaving = signal(false);

  // Geofence polygons pickable as a "geofence" rule's value. Loaded once per
  // building when Edit Style opens (they're building-scoped, not style-scoped).
  geofences = signal<Geofence[]>([]);
  geofencesLoading = signal(false);
  /** Building the current `geofences` list belongs to (cache key). */
  private geofencesBuildingId: number | null = null;

  // Users pickable as a Style Action's alert recipient — same lifecycle as
  // the geofence list (building-scoped, loaded when Edit Style opens).
  actionUsers = signal<ActionUser[]>([]);
  actionUsersLoading = signal(false);
  /** Building the current `actionUsers` list belongs to (cache key). */
  private actionUsersBuildingId: number | null = null;

  // Icon library shared by both style modals. Loaded when Edit Layer opens
  // (layer-scoped) so flipping between styles inside a layer doesn't refetch.
  layerIcons = signal<LayerIcon[]>([]);
  loadingLayerIcons = signal(false);
  iconUploading = signal(false);
  /** Layer the icons + per-layer style mutations are scoped to. */
  currentLayerId = signal<number | null>(null);

  // Generic confirm modal
  confirmState = signal<{
    title: string;
    message: string;
    confirmLabel: string;
    confirmRunning: boolean;
    onConfirm: () => void;
  } | null>(null);

  // ----------------------------------------------------------------------
  // Derived helpers
  // ----------------------------------------------------------------------

  /** agentId → list of layer names — drives the "Layer" column on the
   *  All Trackers table. */
  readonly layerNamesByAgentId = computed<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    for (const layer of this.buildingLayers()) {
      const id = layer.agentId;
      if (!id) continue;
      const name = layer.name ?? `Layer #${layer.id}`;
      (map[id] ||= []).push(name);
    }
    for (const id of Object.keys(map)) {
      map[id] = Array.from(new Set(map[id]));
    }
    return map;
  });

  layerNamesForAgent(agentId: string): string[] {
    return this.layerNamesByAgentId()[agentId] ?? [];
  }

  /** id → Agent map for the level pane's "Tracker Name" column. */
  readonly agentsById = computed<Map<string, Agent>>(
    () => new Map(this.agents().map(a => [a.id, a]))
  );

  agentNameForId(agentId: string | null | undefined): string | null {
    if (!agentId) return null;
    return this.agentsById().get(agentId)?.name ?? null;
  }

  fleetVersionLabel(v: number | null | undefined): string {
    if (v === 1) return '1 — Legacy WLI';
    if (v === 2) return '2 — Fleet API';
    return '—';
  }

  /** Whether the global spinner overlay is visible. True whenever any
   *  read-side fetch is in flight. */
  readonly fetching = computed<boolean>(
    () =>
      this.loadingLevels() ||
      this.loadingAgents() ||
      this.loadingBuildingLayers() ||
      this.loadingLayersForLevelId() !== null ||
      this.layerModalLoading() ||
      this.loadingLayerStyles() ||
      this.loadingStyleRules() ||
      this.loadingStyleActions() ||
      this.loadingLayerIcons() ||
      this.iconUploading()
  );

  /** Ref into the StyleModalComponent so we can sync a successful icon
   *  upload + clear the rule/action drafts after a successful POST. */
  @ViewChild(StyleModalComponent) styleModalRef?: StyleModalComponent;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private buildingContext: BuildingContextService,
    private fleet: FleetService,
    private toastr: ToastrService
  ) {
    // Levels + (in parallel) agents + (in parallel) all-building layers
    // whenever the navbar building changes.
    toObservable(this.buildingContext.selected)
      .pipe(
        tap(building => {
          this.activeTab.set('trackers');
          this.levels.set([]);
          this.layersByLevel.set({});
          this.buildingLayers.set([]);
          this.agents.set([]);
          this.loadingLevels.set(!!building);
          this.loadingAgents.set(!!building);
          this.loadingBuildingLayers.set(!!building);
        }),
        switchMap(building =>
          building
            ? this.fleet.getLiveLevels(building.id).pipe(
                catchError(() => {
                  this.toastr.error('Failed to load live levels.');
                  return of<FleetLevel[]>([]);
                })
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(levels => {
        this.levels.set(levels);
        this.loadingLevels.set(false);
      });

    toObservable(this.buildingContext.selected)
      .pipe(
        switchMap(building =>
          building
            ? this.fleet.getAgentsForBuilding(building.id).pipe(
                catchError(() => {
                  this.toastr.error('Failed to load agents.');
                  return of<Agent[]>([]);
                })
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(agents => {
        this.agents.set(agents);
        this.loadingAgents.set(false);
      });

    toObservable(this.buildingContext.selected)
      .pipe(
        switchMap(building =>
          building
            ? this.fleet.getLayersForBuilding(building.id).pipe(
                catchError(() => {
                  this.toastr.error('Failed to load building layers.');
                  return of<FleetLayer[]>([]);
                })
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(layers => {
        this.buildingLayers.set(layers);
        this.loadingBuildingLayers.set(false);
      });

    // Lazy-load layers for the active level tab. `queueMicrotask` defers
    // the signal writes inside fetchLayersForLevel past the effect so we
    // don't trip Angular's "Writing to signals is not allowed in a
    // `computed` or an `effect`" guard.
    effect(() => {
      const tab = this.activeTab();
      const building = this.buildingContext.selected();
      if (!building || tab === 'trackers') return;
      if (this.layersByLevel()[tab] === undefined) {
        queueMicrotask(() => this.fetchLayersForLevel(building.id, tab));
      }
    });
  }

  // ----------------------------------------------------------------------
  // Tabs / level helpers
  // ----------------------------------------------------------------------

  selectTab(tab: 'trackers' | number): void {
    this.activeTab.set(tab);
  }

  activeLevelId(): number | null {
    const t = this.activeTab();
    return typeof t === 'number' ? t : null;
  }

  private fetchLayersForLevel(buildingId: number, levelId: number): void {
    this.loadingLayersForLevelId.set(levelId);
    this.fleet.getLayersForLevel(buildingId, levelId).subscribe({
      next: layers => {
        this.layersByLevel.update(map => ({ ...map, [levelId]: layers }));
        this.loadingLayersForLevelId.set(null);
      },
      error: () => {
        this.toastr.error('Failed to load layers for this level.');
        this.loadingLayersForLevelId.set(null);
      }
    });
  }

  layersForSelectedLevel(): FleetLayer[] {
    const id = this.activeLevelId();
    if (id == null) return [];
    return this.layersByLevel()[id] ?? [];
  }

  private refreshBuildingLayers(): void {
    const building = this.selectedBuilding();
    if (!building) return;
    this.loadingBuildingLayers.set(true);
    this.fleet.getLayersForBuilding(building.id).subscribe({
      next: layers => {
        this.buildingLayers.set(layers);
        this.loadingBuildingLayers.set(false);
      },
      error: () => {
        this.toastr.error('Failed to refresh layers.');
        this.loadingBuildingLayers.set(false);
      }
    });
  }

  private refreshAgents(): void {
    const building = this.selectedBuilding();
    if (!building) return;
    this.loadingAgents.set(true);
    this.fleet.getAgentsForBuilding(building.id).subscribe({
      next: agents => {
        this.agents.set(agents);
        this.loadingAgents.set(false);
      },
      error: () => {
        this.toastr.error('Failed to refresh agents.');
        this.loadingAgents.set(false);
      }
    });
  }

  // ----------------------------------------------------------------------
  // Delete layer (level tab row)
  // ----------------------------------------------------------------------

  deleteLayer(layer: FleetLayer): void {
    this.openConfirm({
      title: 'Delete layer?',
      message:
        `You're about to permanently delete layer "${layer.name || layer.id}" along with ` +
        `all of its features, styles, and level mappings. This cannot be undone.`,
      confirmLabel: 'Delete layer',
      run: done => {
        this.fleet.deleteLayer(layer.id).subscribe({
          next: () => {
            done();
            this.toastr.success(`Layer "${layer.name}" deleted.`);
            const levelId = this.activeLevelId();
            const building = this.buildingContext.selected();
            if (levelId != null && building) {
              this.layersByLevel.update(map => {
                const next = { ...map };
                delete next[levelId];
                return next;
              });
              this.fetchLayersForLevel(building.id, levelId);
            }
            this.refreshBuildingLayers();
          },
          error: err => {
            done();
            const msg = err?.error?.message || err?.message || 'Delete failed.';
            this.toastr.error(`Could not delete layer: ${msg}`);
          }
        });
      }
    });
  }

  // ----------------------------------------------------------------------
  // Layer modal (LayerModalComponent) — handles both Add and Edit
  // ----------------------------------------------------------------------

  /** Add Layer entry point. Opens the modal in 'add' mode and fetches the
   *  building's unbound trackers for the dropdown. */
  openAddLayer(): void {
    const building = this.selectedBuilding();
    const levelId = this.activeLevelId();
    if (!building || levelId == null) {
      this.toastr.error('Pick a level tab first.');
      return;
    }
    this.layerModalMode.set('add');
    this.currentEditLayer.set(null);
    this.assignableAgents.set([]);
    this.layerStyles.set([]);
    this.layerIcons.set([]);
    this.currentLayerId.set(null);
    this.layerModalOpen.set(true);
    this.layerModalLoading.set(true);

    this.fleet.getAssignableAgents(building.id, null).subscribe({
      next: agents => {
        this.assignableAgents.set(agents);
        this.layerModalLoading.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load assignable trackers.');
        this.layerModalLoading.set(false);
      }
    });
  }

  /** Edit Layer entry point. Opens the modal in 'edit' mode and kicks off
   *  four parallel fetches: layer detail, assignable agents (incl. current),
   *  styles list, and icon library. */
  openEditLayer(layer: FleetLayer): void {
    const building = this.selectedBuilding();
    if (!building) {
      this.toastr.error('Pick a building from the top navbar first.');
      return;
    }
    this.layerModalMode.set('edit');
    this.layerModalOpen.set(true);
    this.layerModalLoading.set(true);
    this.currentEditLayer.set({
      id: layer.id,
      buildingId: building.id,
      name: layer.name ?? '',
      agentId: layer.agentId ?? null,
      updateFrequency: 5
    });
    this.assignableAgents.set([]);
    this.currentLayerId.set(layer.id);

    this.fleet.getLayer(layer.id).subscribe({
      next: detail => {
        this.currentEditLayer.set(detail);
        this.layerModalLoading.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load layer details.');
        this.layerModalLoading.set(false);
      }
    });

    this.fleet.getAssignableAgents(building.id, layer.agentId ?? null).subscribe({
      next: agents => {
        this.assignableAgents.set(agents);
        this.layerModalLoading.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load assignable trackers.');
        this.layerModalLoading.set(false);
      }
    });

    this.loadingLayerStyles.set(true);
    this.layerStyles.set([]);
    this.fleet.getStylesForLayer(layer.id).subscribe({
      next: styles => {
        this.layerStyles.set(styles);
        this.loadingLayerStyles.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load styles for this layer.');
        this.loadingLayerStyles.set(false);
      }
    });

    this.loadingLayerIcons.set(true);
    this.layerIcons.set([]);
    this.fleet.getLayerIcons(layer.id).subscribe({
      next: icons => {
        this.layerIcons.set(icons);
        this.loadingLayerIcons.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load icon library.');
        this.loadingLayerIcons.set(false);
      }
    });
  }

  closeLayerModal(): void {
    if (this.layerModalSaving()) return;
    this.layerModalOpen.set(false);
    this.currentEditLayer.set(null);
    this.assignableAgents.set([]);
    this.layerStyles.set([]);
    this.layerIcons.set([]);
    this.currentLayerId.set(null);
  }

  /** Unified save handler — branches on mode. Add posts to the
   *  level-scoped endpoint; Edit PUTs to the layer-id endpoint. */
  onLayerModalSave(payload: LayerModalSavePayload): void {
    const building = this.selectedBuilding();
    const levelId = this.activeLevelId();
    const isAdd = this.layerModalMode() === 'add';

    if (isAdd) {
      if (!building || levelId == null) {
        this.toastr.error('Pick a level tab first.');
        return;
      }
      this.layerModalSaving.set(true);
      this.fleet.addLayerToLevel(building.id, levelId, payload).subscribe({
        next: () => {
          this.layerModalSaving.set(false);
          this.closeLayerModal();
          this.toastr.success('Layer added.');
          this.layersByLevel.update(map => {
            const next = { ...map };
            delete next[levelId];
            return next;
          });
          this.fetchLayersForLevel(building.id, levelId);
          this.refreshBuildingLayers();
        },
        error: err => {
          this.layerModalSaving.set(false);
          const msg = err?.error?.message || err?.message || 'Add failed.';
          this.toastr.error(`Could not add layer: ${msg}`);
        }
      });
      return;
    }

    // Edit mode — the modal validates agentId is set before emitting.
    const layer = this.currentEditLayer();
    if (!layer) return;
    if (!payload.agentId) {
      this.toastr.error('A tracker must be selected.');
      return;
    }
    this.layerModalSaving.set(true);
    this.fleet.updateLayer(layer.id, {
      name: payload.name,
      agentId: payload.agentId,
      updateFrequency: payload.updateFrequency
    }).subscribe({
      next: () => {
        this.layerModalSaving.set(false);
        this.closeLayerModal();
        this.toastr.success('Layer saved.');

        if (building && levelId != null) {
          this.layersByLevel.update(map => {
            const next = { ...map };
            delete next[levelId];
            return next;
          });
          this.fetchLayersForLevel(building.id, levelId);
        }
        this.refreshBuildingLayers();
      },
      error: err => {
        this.layerModalSaving.set(false);
        const msg =
          err?.error?.message ||
          err?.error?.message ||
          err?.message ||
          'Save failed.';
        this.toastr.error(msg);
      }
    });
  }

  // ----------------------------------------------------------------------
  // Add / Edit Tracker (NewTrackerComponent + UpdateTrackerComponent)
  // ----------------------------------------------------------------------

  openCreateAgent(): void {
    this.trackerModalMode.set('create');
    this.currentEditAgent.set(null);
    this.trackerModalOpen.set(true);
  }

  openEditAgent(agent: Agent): void {
    this.trackerModalMode.set('edit');
    this.currentEditAgent.set(agent);
    this.trackerModalOpen.set(true);
  }

  closeTrackerModal(): void {
    if (this.trackerModalSaving()) return;
    this.trackerModalOpen.set(false);
    this.currentEditAgent.set(null);
  }

  onTrackerModalSave(payload: AgentSavePayload): void {
    const building = this.selectedBuilding();
    const isCreate = this.trackerModalMode() === 'create';
    if (isCreate && !building) {
      this.toastr.error('Pick a building from the top navbar first.');
      return;
    }
    this.trackerModalSaving.set(true);
    const obs = isCreate
      ? this.fleet.createAgent(building!.id, payload)
      : this.fleet.updateAgent(payload.id, payload);
    obs.subscribe({
      next: () => {
        this.trackerModalSaving.set(false);
        this.trackerModalOpen.set(false);
        this.currentEditAgent.set(null);
        this.toastr.success(isCreate ? 'Agent created.' : 'Agent updated.');
        this.refreshAgents();
      },
      error: err => {
        this.trackerModalSaving.set(false);
        const msg =
          err?.error?.message ||
          err?.error?.message ||
          err?.message ||
          'Save failed.';
        this.toastr.error(msg);
      }
    });
  }

  /** Per-agent in-flight flag so the button locks while the POST is out — a
   *  double-click can't fire two dispatch calls and the row lights up while
   *  we wait for the fleet-connector fan-out. Keyed by agent.id so multiple
   *  rows can be in-flight independently. */
  requestingGpsFor = signal<Set<string>>(new Set<string>());

  requestGpsData(agent: Agent): void {
    // Only Fleet v2 trackers route through the new fleet-connector control
    // plane that turns this call into a Piccolo GPS_REQUEST over Redis.
    // v1 (legacy connector) and everything else can't be reached this way.
    if (agent.fleetVersion !== 2) {
      this.toastr.warning('Manual GPS Request only works for Fleet v2 trackers.');
      return;
    }
    if (this.requestingGpsFor().has(agent.id)) return;

    this.requestingGpsFor.update(s => {
      const next = new Set(s);
      next.add(agent.id);
      return next;
    });
    this.fleet.requestGpsData(agent.id).subscribe({
      next: res => {
        this.requestingGpsFor.update(s => {
          const next = new Set(s);
          next.delete(agent.id);
          return next;
        });
        this.toastr.success(res.message);
      },
      error: err => {
        this.requestingGpsFor.update(s => {
          const next = new Set(s);
          next.delete(agent.id);
          return next;
        });
        // Backend returns a shaped ApiResponse with `message` for the 404/429/503
        // status codes we care about; fall back to the raw error only when
        // that's missing so we don't strand the operator with "Http failure".
        const msg =
          err?.error?.message ||
          err?.message ||
          'Failed to send GPS request.';
        this.toastr.error(msg);
      },
    });
  }

  deleteAgent(agent: Agent): void {
    this.openConfirm({
      title: 'Delete agent?',
      message:
        `You're about to permanently delete agent "${agent.name || agent.id}". ` +
        `Any Layers currently pointing at this agent will be detached (agentId set to NULL). ` +
        `This cannot be undone.`,
      confirmLabel: 'Delete agent',
      run: done => {
        this.fleet.deleteAgent(agent.id).subscribe({
          next: () => {
            done();
            this.toastr.success(`Agent "${agent.name || agent.id}" deleted.`);
            this.refreshAgents();
            this.layersByLevel.set({});
            const building = this.selectedBuilding();
            const levelId = this.activeLevelId();
            if (building && levelId != null) {
              this.fetchLayersForLevel(building.id, levelId);
            }
            this.refreshBuildingLayers();
          },
          error: err => {
            done();
            const msg = err?.error?.message || err?.message || 'Delete failed.';
            this.toastr.error(`Could not delete agent: ${msg}`);
          }
        });
      }
    });
  }

  // ----------------------------------------------------------------------
  // Add Style (NewStyleComponent) — opened from UpdateLayer's "Add Style"
  // ----------------------------------------------------------------------

  // Opens the StyleModal in 'add' mode (no styleId yet — Rules + Actions
  // sections are hidden until the first save flips it into 'edit' mode).
  onAddStyleFromLayer(): void {
    if (this.currentLayerId() == null) {
      this.toastr.error('Open the layer first before adding a style.');
      return;
    }
    this.styleModalMode.set('add');
    this.currentEditStyle.set(null);
    this.styleRules.set([]);
    this.styleActions.set([]);
    this.styleModalOpen.set(true);
  }

  closeStyleModal(): void {
    if (this.styleModalSaving()) return;
    this.styleModalOpen.set(false);
    this.currentEditStyle.set(null);
    this.styleRules.set([]);
    this.styleActions.set([]);
  }

  // ----------------------------------------------------------------------
  // Edit Style (UpdateStyleComponent) — opened from UpdateLayer's styles list
  // ----------------------------------------------------------------------

  // Opens the StyleModal in 'edit' mode and hydrates Rules + Actions in
  // parallel. The icon library is already loaded by openEditLayer().
  onEditStyleFromLayer(style: Style): void {
    this.styleModalMode.set('edit');
    this.currentEditStyle.set(style);
    this.styleRules.set([]);
    this.styleActions.set([]);
    this.styleModalOpen.set(true);
    this.fetchGeofencesForCurrentBuilding();
    this.fetchActionUsersForCurrentBuilding();

    this.loadingStyleRules.set(true);
    this.loadingStyleActions.set(true);
    this.fleet.getStyleRules(style.id).subscribe({
      next: rules => {
        this.styleRules.set(rules);
        this.loadingStyleRules.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load style rules.');
        this.loadingStyleRules.set(false);
      }
    });
    this.fleet.getStyleActions(style.id).subscribe({
      next: actions => {
        this.styleActions.set(actions);
        this.loadingStyleActions.set(false);
      },
      error: () => {
        this.toastr.error('Failed to load style actions.');
        this.loadingStyleActions.set(false);
      }
    });
  }

  /** Loads the building's geofence polygons for the rule-value dropdown.
   *  Cached per building — reopening styles in the same building is free. */
  private fetchGeofencesForCurrentBuilding(): void {
    const building = this.selectedBuilding();
    if (!building) return;
    if (this.geofencesBuildingId === building.id) return;
    this.geofencesLoading.set(true);
    this.fleet.getGeofencesForBuilding(building.id).subscribe({
      next: list => {
        this.geofences.set(list);
        this.geofencesBuildingId = building.id;
        this.geofencesLoading.set(false);
      },
      error: () => {
        this.geofences.set([]);
        this.geofencesBuildingId = null;
        this.geofencesLoading.set(false);
        this.toastr.error('Failed to load geofences for this building.');
      }
    });
  }

  /** Loads the building's alert-recipient users for the Actions "User"
   *  dropdown. Cached per building, same as the geofence list. */
  private fetchActionUsersForCurrentBuilding(): void {
    const building = this.selectedBuilding();
    if (!building) return;
    if (this.actionUsersBuildingId === building.id) return;
    this.actionUsersLoading.set(true);
    this.fleet.getActionUsersForBuilding(building.id).subscribe({
      next: list => {
        this.actionUsers.set(list);
        this.actionUsersBuildingId = building.id;
        this.actionUsersLoading.set(false);
      },
      error: () => {
        this.actionUsers.set([]);
        this.actionUsersBuildingId = null;
        this.actionUsersLoading.set(false);
        this.toastr.error('Failed to load users for this building.');
      }
    });
  }

  onStyleModalSave(payload: StyleUpdatePayload): void {
    const layerId = this.currentLayerId();
    const isAdd = this.styleModalMode() === 'add';
    if (isAdd && layerId == null) {
      this.toastr.error('Layer context missing.');
      return;
    }
    const style = this.currentEditStyle();
    if (!isAdd && !style) return;

    this.styleModalSaving.set(true);
    const obs = isAdd
      ? this.fleet.addStyleToLayer(layerId!, payload)
      : this.fleet.updateStyle(style!.id, payload);
    obs.subscribe({
      next: result => {
        this.styleModalSaving.set(false);
        this.styleModalOpen.set(false);
        this.currentEditStyle.set(null);
        if (isAdd) {
          this.layerStyles.update(list => [...list, result]);
          this.toastr.success('Style created.');
        } else {
          this.layerStyles.update(list =>
            list.map(s => (s.id === result.id ? result : s))
          );
          this.toastr.success('Style saved.');
        }
      },
      error: err => {
        this.styleModalSaving.set(false);
        const msg = err?.error?.message || err?.message || 'Save failed.';
        this.toastr.error(msg);
      }
    });
  }

  // Style row deletion (called from UpdateLayerComponent's styles list).
  onDeleteStyleFromLayer(style: Style): void {
    this.openConfirm({
      title: 'Delete style?',
      message:
        `Remove the "${style.name || `Style #${style.id}`}" style? ` +
        `This also deletes its rules + actions, and clears the style from ` +
        `any features using it. This cannot be undone.`,
      confirmLabel: 'Delete style',
      run: done => {
        this.fleet.deleteStyle(style.id).subscribe({
          next: () => {
            done();
            this.layerStyles.update(list => list.filter(s => s.id !== style.id));
            this.toastr.success('Style deleted.');
          },
          error: err => {
            done();
            const msg = err?.error?.message || err?.message || 'Delete failed.';
            this.toastr.error(msg);
          }
        });
      }
    });
  }

  // ----------------------------------------------------------------------
  // Rules + Actions — relayed events from UpdateStyleComponent
  // ----------------------------------------------------------------------

  onAddRule(payload: StyleRuleCreatePayload): void {
    const style = this.currentEditStyle();
    if (!style) return;
    this.ruleSaving.set(true);
    this.fleet.createStyleRule(style.id, payload).subscribe({
      next: created => {
        this.ruleSaving.set(false);
        this.styleRules.update(list => [...list, created]);
        this.styleModalRef?.clearRuleDraft();
        this.toastr.success('Rule added.');
      },
      error: err => {
        this.ruleSaving.set(false);
        const msg = err?.error?.message || err?.message || 'Add failed.';
        this.toastr.error(msg);
      }
    });
  }

  onDeleteRule(rule: StyleRule): void {
    this.openConfirm({
      title: 'Delete rule?',
      message:
        `Remove the rule "${rule.fieldName} ${rule.fieldCondition} ${rule.fieldValue}"?`,
      confirmLabel: 'Delete rule',
      run: done => {
        this.fleet.deleteStyleRule(rule.id).subscribe({
          next: () => {
            done();
            this.styleRules.update(list => list.filter(r => r.id !== rule.id));
            this.toastr.success('Rule deleted.');
          },
          error: err => {
            done();
            const msg = err?.error?.message || err?.message || 'Delete failed.';
            this.toastr.error(msg);
          }
        });
      }
    });
  }

  onAddAction(payload: StyleActionCreatePayload): void {
    const style = this.currentEditStyle();
    if (!style) return;
    this.actionSaving.set(true);
    this.fleet.createStyleAction(style.id, payload).subscribe({
      next: created => {
        this.actionSaving.set(false);
        this.styleActions.update(list => [...list, created]);
        this.styleModalRef?.clearActionDraft();
        this.toastr.success('Action added.');
      },
      error: err => {
        this.actionSaving.set(false);
        const msg = err?.error?.message || err?.message || 'Add failed.';
        this.toastr.error(msg);
      }
    });
  }

  onDeleteAction(action: StyleAction): void {
    this.openConfirm({
      title: 'Delete action?',
      message: `Remove the "${action.actionName}" action?`,
      confirmLabel: 'Delete action',
      run: done => {
        this.fleet.deleteStyleAction(action.id).subscribe({
          next: () => {
            done();
            this.styleActions.update(list => list.filter(a => a.id !== action.id));
            this.toastr.success('Action deleted.');
          },
          error: err => {
            done();
            const msg = err?.error?.message || err?.message || 'Delete failed.';
            this.toastr.error(msg);
          }
        });
      }
    });
  }

  // ----------------------------------------------------------------------
  // Icon upload — relayed from whichever style modal is open
  // ----------------------------------------------------------------------

  /** Single icon-upload handler — whichever mode the StyleModal is in,
   *  we relay the newly-uploaded icon back via its ref. */
  onStyleIconFileChosen(file: File): void {
    this.uploadIconAnd(icon => this.styleModalRef?.selectUploadedIcon(icon), file);
  }

  private uploadIconAnd(then: (icon: LayerIcon) => void, file: File): void {
    const layerId = this.currentLayerId();
    if (layerId == null) {
      this.toastr.error('Open the layer first before uploading an icon.');
      return;
    }
    this.iconUploading.set(true);
    this.fleet.uploadLayerIcon(layerId, file).subscribe({
      next: icon => {
        this.iconUploading.set(false);
        this.layerIcons.update(list => {
          const filtered = list.filter(i => i.filename !== icon.filename);
          return [...filtered, icon].sort((a, b) =>
            a.filename.localeCompare(b.filename)
          );
        });
        then(icon);
        this.toastr.success(`Uploaded ${icon.filename}.`);
      },
      error: err => {
        this.iconUploading.set(false);
        const msg = err?.error?.message || err?.message || 'Upload failed.';
        this.toastr.error(msg);
      }
    });
  }

  // ----------------------------------------------------------------------
  // Generic confirm modal
  // ----------------------------------------------------------------------

  private openConfirm(opts: {
    title: string;
    message: string;
    confirmLabel: string;
    run: (done: () => void) => void;
  }): void {
    this.confirmState.set({
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel,
      confirmRunning: false,
      onConfirm: () => {
        this.confirmState.update(s => (s ? { ...s, confirmRunning: true } : s));
        opts.run(() => this.confirmState.set(null));
      }
    });
  }

  closeConfirm(): void {
    const s = this.confirmState();
    if (s?.confirmRunning) return;
    this.confirmState.set(null);
  }

  runConfirm(): void {
    const s = this.confirmState();
    if (!s || s.confirmRunning) return;
    s.onConfirm();
  }
}
